<template>
  <div class="dash-topbar">
    <div class="dash-topbar-brand">
      <i class="ti ti-device-tv"></i> GoPlayTV
    </div>
    <div class="dash-topbar-actions">
      <RouterLink to="/" class="dash-topbar-link">
        <i class="ti ti-world"></i> View Site
      </RouterLink>
      <button class="dash-topbar-link" @click="handleLogout">
        <i class="ti ti-logout"></i> Logout
      </button>
    </div>
  </div>

  <div class="dashboard-layout">
    <!-- Sidebar -->
    <aside class="dash-sidebar">
      <div class="dash-profile">
        <div class="dash-avatar">{{ initials }}</div>
        <div>
          <div class="dash-name">{{ authStore.user?.name }}</div>
          <div class="dash-email">{{ authStore.user?.email }}</div>
        </div>
      </div>

      <nav class="dash-nav">
        <button
          v-for="tab in tabs" :key="tab.id"
          class="dash-nav-item"
          :class="{ active: activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          <i :class="tab.icon"></i> {{ tab.label }}
        </button>
      </nav>

      <button class="dash-nav-item dash-logout mt-auto" @click="handleLogout">
        <i class="ti ti-logout"></i> Logout
      </button>
    </aside>

    <!-- Main content -->
    <main class="dash-main">

      <!-- OVERVIEW -->
      <div v-if="activeTab === 'overview'">
        <h2 class="dash-heading">Welcome back, {{ authStore.user?.name }}</h2>

        <div v-if="loading" class="dash-loading">Loading your account...</div>

        <template v-else>
          <div class="dash-stats-row">
            <div class="dash-stat-card">
              <div class="dash-stat-icon" style="background:rgba(201,14,14,0.15)">
                <i class="ti ti-device-tv" style="color:#c90e0e"></i>
              </div>
              <div>
                <div class="dash-stat-value">{{ subscription ? subscription.package?.name : 'None' }}</div>
                <div class="dash-stat-label">Current Plan</div>
              </div>
            </div>
            <div class="dash-stat-card">
              <div class="dash-stat-icon" style="background:rgba(25,135,84,0.15)">
                <i class="ti ti-circle-check" :style="{ color: subStatusColor }"></i>
              </div>
              <div>
                <div class="dash-stat-value text-capitalize">{{ subscription?.status ?? 'No subscription' }}</div>
                <div class="dash-stat-label">Status</div>
              </div>
            </div>
            <div class="dash-stat-card">
              <div class="dash-stat-icon" style="background:rgba(192,192,192,0.1)">
                <i class="ti ti-calendar-event" style="color:#c0c0c0"></i>
              </div>
              <div>
                <div class="dash-stat-value">{{ subscription?.expires_at ? formatDate(subscription.expires_at) : '—' }}</div>
                <div class="dash-stat-label">Expires</div>
              </div>
            </div>
            <div class="dash-stat-card">
              <div class="dash-stat-icon" style="background:rgba(201,14,14,0.15)">
                <i class="ti ti-wifi" style="color:#c90e0e"></i>
              </div>
              <div>
                <div class="dash-stat-value">{{ line ? 'Active' : 'Not assigned' }}</div>
                <div class="dash-stat-label">IPTV Line</div>
              </div>
            </div>
          </div>

          <!-- Expiry progress bar -->
          <div v-if="subscription?.expires_at" class="dash-card mb-4">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="dash-card-label">Subscription Time Remaining</span>
              <span class="dash-badge" :class="daysLeft > 7 ? 'badge-green' : 'badge-red'">{{ daysLeft }} days left</span>
            </div>
            <div class="dash-progress-track">
              <div class="dash-progress-bar" :style="{ width: progressPct + '%', background: daysLeft > 7 ? '#198754' : '#c90e0e' }"></div>
            </div>
            <div class="d-flex justify-content-between mt-1">
              <small class="text-silver">{{ subscription.package?.name }}</small>
              <small class="text-silver">{{ formatDate(subscription.expires_at) }}</small>
            </div>
          </div>

          <!-- No subscription CTA -->
          <div v-if="!subscription" class="dash-card text-center py-5">
            <i class="ti ti-device-tv" style="font-size:3rem;color:#c90e0e;display:block;margin-bottom:1rem"></i>
            <h4 style="color:#fff;margin-bottom:0.5rem">You don't have an active subscription</h4>
            <p class="text-silver mb-4">Choose a plan to unlock your IPTV access</p>
            <RouterLink to="/" class="btn"><span class="border-style">View Plans</span></RouterLink>
          </div>

          <!-- Quick IPTV line preview -->
          <div v-if="line && line.status !== 'pending_assignment'" class="dash-card">
            <h5 class="dash-card-title">IPTV Line</h5>
            <div v-if="line.portal_url" class="dash-credential-row">
              <span class="dash-credential-label">Portal URL</span>
              <div class="dash-credential-value">
                <code>{{ line.portal_url }}</code>
                <button class="dash-copy-btn" @click="copy(line.portal_url, 'portal')">
                  <i :class="copied === 'portal' ? 'ti ti-check' : 'ti ti-copy'"></i>
                </button>
              </div>
            </div>
            <div v-if="!line.portal_url || line.m3u_url" class="dash-credential-row">
              <span class="dash-credential-label">M3U URL</span>
              <div class="dash-credential-value">
                <code>{{ line.m3u_url || '—' }}</code>
                <button v-if="line.m3u_url" class="dash-copy-btn" @click="copy(line.m3u_url, 'm3u')">
                  <i :class="copied === 'm3u' ? 'ti ti-check' : 'ti ti-copy'"></i>
                </button>
              </div>
            </div>
            <div class="dash-credential-row">
              <span class="dash-credential-label">EPG URL</span>
              <div class="dash-credential-value">
                <code>{{ line.epg_url || '—' }}</code>
                <button v-if="line.epg_url" class="dash-copy-btn" @click="copy(line.epg_url, 'epg')">
                  <i :class="copied === 'epg' ? 'ti ti-check' : 'ti ti-copy'"></i>
                </button>
              </div>
            </div>
            <button class="btn mt-3" @click="activeTab = 'line'"><span class="border-style">View Full Details</span></button>
          </div>

          <div v-else-if="line?.status === 'pending_assignment'" class="dash-card text-center py-4">
            <i class="ti ti-loader-2" style="font-size:2rem;color:#c90e0e;display:block;margin-bottom:0.5rem"></i>
            <p class="text-silver mb-0">Your IPTV line is being set up. We'll have your credentials ready shortly.</p>
          </div>
        </template>
      </div>

      <!-- SUBSCRIPTION -->
      <div v-if="activeTab === 'subscription'">
        <h2 class="dash-heading">My Subscription</h2>

        <div v-if="loading" class="dash-loading">Loading...</div>
        <template v-else-if="subscription">
          <div class="dash-card mb-4">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h4 style="color:#fff;margin:0">{{ subscription.package?.name }}</h4>
              <span class="dash-badge" :class="subscription.status === 'active' ? 'badge-green' : 'badge-orange'">
                {{ subscription.status }}
              </span>
            </div>
            <div class="dash-detail-row"><span>Price</span><strong>${{ subscription.package?.price }}/month</strong></div>
            <div class="dash-detail-row"><span>Duration</span><strong>{{ subscription.package?.duration_days }} days</strong></div>
            <div class="dash-detail-row"><span>Started</span><strong>{{ formatDate(subscription.created_at) }}</strong></div>
            <div class="dash-detail-row"><span>Expires</span><strong>{{ subscription.expires_at ? formatDate(subscription.expires_at) : 'Never' }}</strong></div>
            <div v-if="subscription.invoice_id" class="dash-detail-row"><span>Invoice ID</span><strong>{{ subscription.invoice_id }}</strong></div>

            <div v-if="subscription.expires_at" class="mt-4">
              <div class="d-flex justify-content-between mb-1">
                <small class="text-silver">Time remaining</small>
                <small class="text-silver">{{ daysLeft }} / {{ subscription.package?.duration_days }} days</small>
              </div>
              <div class="dash-progress-track">
                <div class="dash-progress-bar" :style="{ width: progressPct + '%', background: daysLeft > 7 ? '#198754' : '#c90e0e' }"></div>
              </div>
            </div>
          </div>

          <div class="dash-card">
            <h5 class="dash-card-title">Upgrade or Renew</h5>
            <p class="text-silver mb-3">Want more features? Choose a different plan below.</p>
            <RouterLink to="/" class="btn"><span class="border-style">Browse Plans</span></RouterLink>
          </div>
        </template>
        <div v-else class="dash-card text-center py-5">
          <p class="text-silver mb-3">No active subscription.</p>
          <RouterLink to="/" class="btn"><span class="border-style">Browse Plans</span></RouterLink>
        </div>
      </div>

      <!-- IPTV LINE -->
      <div v-if="activeTab === 'line'">
        <h2 class="dash-heading">My IPTV Line</h2>

        <div v-if="loading" class="dash-loading">Loading...</div>

        <!-- No subscription -->
        <div v-else-if="!subscription" class="dash-card text-center py-5">
          <i class="ti ti-device-tv" style="font-size:3rem;color:#c90e0e;display:block;margin-bottom:1rem"></i>
          <h4 style="color:#fff;margin-bottom:0.5rem">No active subscription</h4>
          <p class="text-silver mb-4">Subscribe to a plan to get your IPTV line.</p>
          <RouterLink to="/" class="btn"><span class="border-style">Browse Plans</span></RouterLink>
        </div>

        <!-- Step 1: need MAC address -->
        <template v-else-if="!line || !line.mac_address">
          <div class="dash-setup-steps">
            <div class="dash-step done"><div class="dash-step-num"><i class="ti ti-check"></i></div><span>Subscribed</span></div>
            <div class="dash-step-line"></div>
            <div class="dash-step active"><div class="dash-step-num">2</div><span>Submit MAC</span></div>
            <div class="dash-step-line"></div>
            <div class="dash-step"><div class="dash-step-num">3</div><span>Activation</span></div>
            <div class="dash-step-line"></div>
            <div class="dash-step"><div class="dash-step-num">4</div><span>Watch</span></div>
          </div>

          <div class="dash-card">
            <h5 class="dash-card-title">Step 1 — Download Your IPTV App</h5>
            <p class="text-silver mb-3" style="font-size:0.92rem">Install one of these apps on your device, then open it to find your MAC address or device key.</p>
            <div class="dash-app-grid">
              <a href="https://iboplayer.com" target="_blank" rel="noopener" class="dash-app-card">
                <i class="ti ti-player-play" style="font-size:1.5rem;color:#c90e0e"></i>
                <div class="dash-app-name">iboPlayer</div>
                <div class="dash-app-platform">Android / iOS / Smart TV</div>
              </a>
              <a href="https://tivimate.com" target="_blank" rel="noopener" class="dash-app-card">
                <i class="ti ti-device-tv" style="font-size:1.5rem;color:#c90e0e"></i>
                <div class="dash-app-name">TiviMate</div>
                <div class="dash-app-platform">Android TV / Fire Stick</div>
              </a>
              <a href="https://www.smarters.pro" target="_blank" rel="noopener" class="dash-app-card">
                <i class="ti ti-brand-android" style="font-size:1.5rem;color:#c90e0e"></i>
                <div class="dash-app-name">IPTV Smarters Pro</div>
                <div class="dash-app-platform">Android / iOS</div>
              </a>
              <a href="https://gse-smart-iptv.com" target="_blank" rel="noopener" class="dash-app-card">
                <i class="ti ti-brand-apple" style="font-size:1.5rem;color:#c90e0e"></i>
                <div class="dash-app-name">GSE Smart IPTV</div>
                <div class="dash-app-platform">iOS / macOS</div>
              </a>
            </div>
          </div>

          <div class="dash-card">
            <h5 class="dash-card-title">Step 2 — Submit Your MAC Address / Device Key</h5>
            <p class="text-silver mb-3" style="font-size:0.92rem">
              Open the app → go to <strong style="color:#fff">Settings</strong> → find <strong style="color:#fff">MAC Address</strong> or <strong style="color:#fff">Device Key</strong>.
              It usually looks like <code style="color:#c90e0e">AA:BB:CC:DD:EE:FF</code> or a alphanumeric key.
            </p>
            <div v-if="macError" class="dash-alert dash-alert-danger mb-3">{{ macError }}</div>
            <div v-if="macSaved" class="dash-alert dash-alert-success mb-3">MAC address saved! We'll activate your line shortly.</div>
            <div class="d-flex gap-2 align-items-center" style="max-width:480px">
              <input
                v-model="macInput"
                type="text"
                class="dash-input"
                placeholder="e.g. AA:BB:CC:DD:EE:FF"
                :disabled="macSaving"
              />
              <button class="btn" style="white-space:nowrap" :disabled="macSaving || !macInput.trim()" @click="submitMac">
                <span class="border-style">{{ macSaving ? 'Saving...' : 'Submit' }}</span>
              </button>
            </div>
          </div>
        </template>

        <!-- Step 2: MAC submitted, waiting for activation -->
        <template v-else-if="line.status === 'pending_assignment'">
          <div class="dash-setup-steps">
            <div class="dash-step done"><div class="dash-step-num"><i class="ti ti-check"></i></div><span>Subscribed</span></div>
            <div class="dash-step-line done"></div>
            <div class="dash-step done"><div class="dash-step-num"><i class="ti ti-check"></i></div><span>MAC Submitted</span></div>
            <div class="dash-step-line"></div>
            <div class="dash-step active"><div class="dash-step-num">3</div><span>Activation</span></div>
            <div class="dash-step-line"></div>
            <div class="dash-step"><div class="dash-step-num">4</div><span>Watch</span></div>
          </div>

          <div class="dash-card text-center py-4">
            <div style="font-size:2.5rem;margin-bottom:1rem">⏳</div>
            <h4 style="color:#fff;margin-bottom:0.5rem">Activation in Progress</h4>
            <p class="text-silver mb-3">Your MAC address has been received. Our team is activating your line — this usually takes <strong style="color:#fff">under 1 hour</strong>.</p>
            <div class="dash-mac-display">
              <i class="ti ti-device-desktop"></i>
              <span>{{ line.mac_address }}</span>
              <button class="dash-copy-btn" @click="copy(line.mac_address, 'mac')">
                <i :class="copied === 'mac' ? 'ti ti-check' : 'ti ti-copy'"></i>
              </button>
            </div>
            <p class="text-silver mt-3" style="font-size:0.82rem">Need to change your MAC address? <button class="dash-text-btn" @click="line.mac_address = ''">Update it</button></p>
          </div>
        </template>

        <!-- Step 3: Active — show credentials -->
        <template v-else>
          <div class="dash-setup-steps">
            <div class="dash-step done"><div class="dash-step-num"><i class="ti ti-check"></i></div><span>Subscribed</span></div>
            <div class="dash-step-line done"></div>
            <div class="dash-step done"><div class="dash-step-num"><i class="ti ti-check"></i></div><span>MAC Submitted</span></div>
            <div class="dash-step-line done"></div>
            <div class="dash-step done"><div class="dash-step-num"><i class="ti ti-check"></i></div><span>Activated</span></div>
            <div class="dash-step-line done"></div>
            <div class="dash-step done"><div class="dash-step-num"><i class="ti ti-check"></i></div><span>Watch</span></div>
          </div>

          <div class="dash-card">
            <div class="d-flex align-items-center gap-2 mb-4 flex-wrap">
              <span style="color:#4ade80;font-size:1.3rem">●</span>
              <h5 class="dash-card-title mb-0">Your Line is Active</h5>
              <span v-if="line.expires_at" class="text-silver ms-auto" style="font-size:0.85rem">
                <i class="ti ti-calendar"></i> Expires {{ formatDate(line.expires_at) }}
              </span>
            </div>

            <div v-if="line.portal_url" class="dash-cred-block">
              <div class="dash-cred-header">
                <i class="ti ti-device-desktop"></i> Portal URL — enter this in your device / app
                <button class="dash-copy-btn ms-auto" @click="copy(line.portal_url, 'portal')">
                  <i :class="copied === 'portal' ? 'ti ti-check' : 'ti ti-copy'"></i> {{ copied === 'portal' ? 'Copied!' : 'Copy' }}
                </button>
              </div>
              <code class="dash-cred-value">{{ line.portal_url }}</code>
            </div>

            <div v-if="line.server_url" class="dash-cred-block">
              <div class="dash-cred-header">
                <i class="ti ti-server"></i> Server URL
                <button class="dash-copy-btn ms-auto" @click="copy(line.server_url, 'srv')">
                  <i :class="copied === 'srv' ? 'ti ti-check' : 'ti ti-copy'"></i> {{ copied === 'srv' ? 'Copied!' : 'Copy' }}
                </button>
              </div>
              <code class="dash-cred-value">{{ line.server_url }}</code>
            </div>

            <div v-if="line.m3u_url" class="dash-cred-block">
              <div class="dash-cred-header">
                <i class="ti ti-list"></i> M3U Playlist URL
                <button class="dash-copy-btn ms-auto" @click="copy(line.m3u_url, 'm3u')">
                  <i :class="copied === 'm3u' ? 'ti ti-check' : 'ti ti-copy'"></i> {{ copied === 'm3u' ? 'Copied!' : 'Copy' }}
                </button>
              </div>
              <code class="dash-cred-value">{{ line.m3u_url }}</code>
            </div>

            <div v-if="line.epg_url" class="dash-cred-block">
              <div class="dash-cred-header">
                <i class="ti ti-calendar"></i> EPG / Guide URL
                <button class="dash-copy-btn ms-auto" @click="copy(line.epg_url, 'epg')">
                  <i :class="copied === 'epg' ? 'ti ti-check' : 'ti ti-copy'"></i> {{ copied === 'epg' ? 'Copied!' : 'Copy' }}
                </button>
              </div>
              <code class="dash-cred-value">{{ line.epg_url }}</code>
            </div>

            <div class="row g-3 mt-1">
              <div v-if="line.username" class="col-md-6">
                <div class="dash-cred-block">
                  <div class="dash-cred-header">
                    <i class="ti ti-user"></i> Username
                    <button class="dash-copy-btn ms-auto" @click="copy(line.username, 'user')">
                      <i :class="copied === 'user' ? 'ti ti-check' : 'ti ti-copy'"></i>
                    </button>
                  </div>
                  <code class="dash-cred-value">{{ line.username }}</code>
                </div>
              </div>
              <div v-if="line.password" class="col-md-6">
                <div class="dash-cred-block">
                  <div class="dash-cred-header">
                    <i class="ti ti-lock"></i> Password
                    <button class="dash-copy-btn ms-auto" @click="copy(line.password, 'pass')">
                      <i :class="copied === 'pass' ? 'ti ti-check' : 'ti ti-copy'"></i>
                    </button>
                  </div>
                  <code class="dash-cred-value">{{ showPassword ? line.password : '••••••••' }}</code>
                  <button class="dash-text-btn" style="padding:0.25rem 1rem;display:block" @click="showPassword = !showPassword">
                    {{ showPassword ? 'Hide' : 'Show password' }}
                  </button>
                </div>
              </div>
            </div>

            <div v-if="line.mac_address" class="dash-cred-block mt-1">
              <div class="dash-cred-header"><i class="ti ti-device-desktop"></i> Registered MAC Address</div>
              <code class="dash-cred-value">{{ line.mac_address }}</code>
            </div>

            <div v-if="line.notes" class="dash-cred-block mt-1">
              <div class="dash-cred-header"><i class="ti ti-notes"></i> Notes from Support</div>
              <p class="text-silver mb-0" style="padding:0.75rem 1rem;font-size:0.9rem">{{ line.notes }}</p>
            </div>
          </div>
        </template>
      </div>

      <!-- REFER & EARN -->
      <div v-if="activeTab === 'refer'">
        <h2 class="dash-heading">Refer &amp; Earn</h2>

        <div v-if="referralLoading" class="dash-loading">Loading your referral details...</div>

        <template v-else-if="referral">
          <div class="dash-card">
            <h5 class="dash-card-title">Share your link, earn free days</h5>
            <p class="text-silver mb-3" style="font-size:0.92rem">
              Invite friends with your personal link. When someone signs up and pays for
              their first subscription, you earn <strong style="color:#fff">{{ referral.reward_days }} free days</strong>.
            </p>

            <label class="dash-field-label">Your referral link</label>
            <div class="d-flex gap-2 align-items-center" style="max-width:560px">
              <input :value="referralLink" type="text" class="dash-input" readonly />
              <button class="btn" style="white-space:nowrap" @click="copyReferralLink">
                <span class="border-style">{{ referralCopied ? 'Copied!' : 'Copy' }}</span>
              </button>
            </div>
            <p class="text-silver mt-2" style="font-size:0.82rem">
              Your code: <code style="color:#c90e0e">{{ referral.code }}</code>
            </p>
          </div>

          <div class="dash-stats-row">
            <div class="dash-stat-card">
              <div class="dash-stat-icon" style="background:rgba(96,165,250,0.15)">
                <i class="ti ti-users" style="color:#60a5fa"></i>
              </div>
              <div>
                <div class="dash-stat-value">{{ referral.total_referred }}</div>
                <div class="dash-stat-label">People Referred</div>
              </div>
            </div>
            <div class="dash-stat-card">
              <div class="dash-stat-icon" style="background:rgba(74,222,128,0.15)">
                <i class="ti ti-circle-check" style="color:#4ade80"></i>
              </div>
              <div>
                <div class="dash-stat-value">{{ referral.qualified_count }}</div>
                <div class="dash-stat-label">Subscribed</div>
              </div>
            </div>
            <div class="dash-stat-card">
              <div class="dash-stat-icon" style="background:rgba(201,14,14,0.15)">
                <i class="ti ti-gift" style="color:#c90e0e"></i>
              </div>
              <div>
                <div class="dash-stat-value">{{ referral.earned_days }}</div>
                <div class="dash-stat-label">Free Days Earned</div>
              </div>
            </div>
          </div>

          <div class="dash-card">
            <p class="text-silver mb-0" style="font-size:0.88rem">
              <i class="ti ti-info-circle" style="color:#60a5fa"></i>
              Earned free days are applied by our team to your subscription. If you have days
              waiting, <RouterLink to="/contact-us" style="color:#c90e0e">contact support</RouterLink>
              to have them added to your line.
            </p>
          </div>
        </template>
      </div>

      <!-- PROFILE -->
      <div v-if="activeTab === 'profile'">
        <h2 class="dash-heading">My Profile</h2>

        <div v-if="profileSaved" class="dash-alert dash-alert-success mb-4">Profile updated successfully.</div>
        <div v-if="profileError" class="dash-alert dash-alert-danger mb-4">{{ profileError }}</div>

        <div class="dash-card mb-4">
          <h5 class="dash-card-title">Account Information</h5>
          <div class="row g-3 mt-1">
            <div class="col-md-6">
              <label class="dash-field-label">Full Name</label>
              <input v-model="profileForm.name" type="text" class="dash-input" />
            </div>
            <div class="col-md-6">
              <label class="dash-field-label">Email Address</label>
              <input v-model="profileForm.email" type="email" class="dash-input" />
            </div>
          </div>
          <button class="btn mt-4" :disabled="profileSaving" @click="saveProfile">
            <span class="border-style">{{ profileSaving ? 'Saving...' : 'Save Changes' }}</span>
          </button>
        </div>

        <div class="dash-card">
          <h5 class="dash-card-title">Change Password</h5>
          <div class="row g-3 mt-1">
            <div class="col-md-4">
              <label class="dash-field-label">New Password</label>
              <input v-model="profileForm.password" type="password" class="dash-input" placeholder="Min. 8 characters" />
            </div>
            <div class="col-md-4">
              <label class="dash-field-label">Confirm Password</label>
              <input v-model="profileForm.password_confirmation" type="password" class="dash-input" placeholder="Repeat password" />
            </div>
          </div>
          <button class="btn mt-4" :disabled="profileSaving" @click="saveProfile">
            <span class="border-style">{{ profileSaving ? 'Saving...' : 'Update Password' }}</span>
          </button>
        </div>
      </div>

    </main>
  </div>

  <div class="dash-footer-copy">
    &copy; {{ new Date().getFullYear() }} GoPlayTV. All rights reserved.
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/store/auth";
import api from "@/services/api";

export default defineComponent({
  name: "DashboardPage",
  components: {},
  setup() {
    const authStore = useAuthStore();
    const router = useRouter();

    const activeTab = ref("overview");
    const loading = ref(true);
    const subscription = ref<any>(null);
    const line = ref<any>(null);
    const copied = ref("");
    const showPassword = ref(false);
    const macInput = ref("");
    const macSaving = ref(false);
    const macSaved = ref(false);
    const macError = ref("");

    const profileForm = ref({
      name: authStore.user?.name || "",
      email: authStore.user?.email || "",
      password: "",
      password_confirmation: "",
    });
    const profileSaving = ref(false);
    const profileSaved = ref(false);
    const profileError = ref("");

    const tabs = [
      { id: "overview", label: "Overview", icon: "ti ti-layout-dashboard" },
      { id: "subscription", label: "Subscription", icon: "ti ti-credit-card" },
      { id: "line", label: "IPTV Line", icon: "ti ti-device-tv" },
      { id: "refer", label: "Refer & Earn", icon: "ti ti-gift" },
      { id: "profile", label: "My Profile", icon: "ti ti-user-circle" },
    ];

    // ── Referral program ──────────────────────────────────────────────
    const referral = ref<any>(null);
    const referralLoading = ref(false);
    const referralCopied = ref(false);

    const referralLink = computed(() =>
      referral.value?.code
        ? `${window.location.origin}/register?ref=${referral.value.code}`
        : ""
    );

    async function loadReferral() {
      if (referral.value) return;
      referralLoading.value = true;
      try {
        const { data } = await api.get("/api/user/referrals");
        referral.value = data;
      } finally {
        referralLoading.value = false;
      }
    }

    function copyReferralLink() {
      if (!referralLink.value) return;
      navigator.clipboard.writeText(referralLink.value);
      referralCopied.value = true;
      setTimeout(() => (referralCopied.value = false), 2000);
    }

    watch(activeTab, (t) => {
      if (t === "refer") loadReferral();
    });

    const initials = computed(() => {
      const name = authStore.user?.name || "";
      return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
    });

    const subStatusColor = computed(() => {
      if (!subscription.value) return "#c0c0c0";
      return subscription.value.status === "active" ? "#198754" : "#ffc107";
    });

    const daysLeft = computed(() => {
      if (!subscription.value?.expires_at) return 0;
      const diff = new Date(subscription.value.expires_at).getTime() - Date.now();
      return Math.max(0, Math.ceil(diff / 86400000));
    });

    const progressPct = computed(() => {
      const total = subscription.value?.package?.duration_days || 30;
      return Math.min(100, Math.round((daysLeft.value / total) * 100));
    });

    onMounted(async () => {
      try {
        const { data } = await api.get("/api/user/dashboard");
        subscription.value = data.subscription;
        line.value = data.line;
      } finally {
        loading.value = false;
      }
    });

    async function handleLogout() {
      await authStore.logout();
      router.push("/login");
    }

    function formatDate(iso: string) {
      return new Date(iso).toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric",
      });
    }

    function copy(text: string, key: string) {
      navigator.clipboard.writeText(text);
      copied.value = key;
      setTimeout(() => (copied.value = ""), 2000);
    }

    async function submitMac() {
      macSaving.value = true;
      macError.value = "";
      macSaved.value = false;
      try {
        const { data } = await api.post("/api/v1/iptv/mac", { mac_address: macInput.value });
        line.value = data.line;
        macSaved.value = true;
        macInput.value = "";
      } catch (e: any) {
        macError.value = e?.response?.data?.message || "Failed to save MAC address.";
      } finally {
        macSaving.value = false;
      }
    }

    async function saveProfile() {
      profileSaving.value = true;
      profileSaved.value = false;
      profileError.value = "";
      try {
        const payload: any = { name: profileForm.value.name, email: profileForm.value.email };
        if (profileForm.value.password) {
          payload.password = profileForm.value.password;
          payload.password_confirmation = profileForm.value.password_confirmation;
        }
        const { data } = await api.patch("/api/user/profile", payload);
        authStore.user = data.user;
        localStorage.setItem("auth_user", JSON.stringify(data.user));
        profileSaved.value = true;
        profileForm.value.password = "";
        profileForm.value.password_confirmation = "";
        setTimeout(() => (profileSaved.value = false), 3000);
      } catch (e: any) {
        profileError.value = e?.response?.data?.message || "Update failed.";
      } finally {
        profileSaving.value = false;
      }
    }

    return {
      authStore, activeTab, tabs, loading, subscription, line, initials,
      subStatusColor, daysLeft, progressPct, copied, showPassword,
      macInput, macSaving, macSaved, macError, submitMac,
      profileForm, profileSaving, profileSaved, profileError,
      handleLogout, formatDate, copy, saveProfile,
      referral, referralLoading, referralLink, referralCopied, copyReferralLink,
    };
  },
});
</script>

<style scoped>
/* Dashboard topbar */
.dash-topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: #201516;
  border-bottom: 1px solid #3a2526;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  z-index: 999;
}
.dash-topbar-brand {
  color: #fff;
  font-weight: 800;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.dash-topbar-brand i { color: #c90e0e; font-size: 1.3rem; }
.dash-topbar-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.dash-topbar-link {
  background: transparent;
  border: none;
  color: #c0c0c0;
  font-size: 0.9rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  text-decoration: none;
  transition: color 0.2s, background 0.2s;
  font-family: inherit;
}
.dash-topbar-link:hover { color: #fff; background: #3a2526; }

/* Footer */
.dash-footer-copy {
  text-align: center;
  color: #555;
  font-size: 0.8rem;
  padding: 1.5rem;
  border-top: 1px solid #3a2526;
}

.dashboard-layout {
  display: flex;
  min-height: 100vh;
  padding-top: 60px;
  background: #0a0102;
}

/* Sidebar */
.dash-sidebar {
  width: 260px;
  flex-shrink: 0;
  background: #201516;
  border-right: 1px solid #3a2526;
  display: flex;
  flex-direction: column;
  padding: 2rem 1.25rem 1.5rem;
  position: sticky;
  top: 60px;
  height: calc(100vh - 60px);
  overflow-y: auto;
}

.dash-profile {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #3a2526;
}

.dash-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #c90e0e;
  color: #fff;
  font-weight: 700;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dash-name {
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  line-height: 1.2;
}
.dash-email {
  color: #c0c0c0;
  font-size: 0.78rem;
  word-break: break-all;
}

.dash-nav {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.dash-nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 1rem;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #c0c0c0;
  cursor: pointer;
  font-size: 0.95rem;
  font-family: inherit;
  text-align: left;
  transition: all 0.2s;
}
.dash-nav-item i { font-size: 1.1rem; }
.dash-nav-item:hover { background: #2b1e1f; color: #fff; }
.dash-nav-item.active { background: #c90e0e; color: #fff; }
.dash-logout { color: #ff6b6b; margin-top: auto; }
.dash-logout:hover { background: rgba(201,14,14,0.2); color: #ff6b6b; }

/* Main area */
.dash-main {
  flex: 1;
  padding: 2.5rem 2rem;
  min-width: 0;
}

.dash-heading {
  color: #fff;
  font-size: 1.6rem;
  font-weight: 800;
  margin-bottom: 1.75rem;
}

.dash-loading { color: #c0c0c0; font-size: 1rem; padding: 2rem 0; }

/* Stats row */
.dash-stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.dash-stat-card {
  background: #201516;
  border: 1px solid #3a2526;
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}
.dash-stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1.4rem;
}
.dash-stat-value { color: #fff; font-weight: 700; font-size: 1rem; line-height: 1.2; }
.dash-stat-label { color: #c0c0c0; font-size: 0.78rem; margin-top: 2px; }

/* Cards */
.dash-card {
  background: #201516;
  border: 1px solid #3a2526;
  border-radius: 12px;
  padding: 1.75rem;
  margin-bottom: 1.25rem;
}
.dash-card-title {
  color: #fff;
  font-weight: 700;
  margin-bottom: 1rem;
  font-size: 1.05rem;
}
.dash-card-label { color: #c0c0c0; font-size: 0.85rem; }

/* Progress */
.dash-progress-track {
  height: 8px;
  background: #3a2526;
  border-radius: 99px;
  overflow: hidden;
}
.dash-progress-bar {
  height: 100%;
  border-radius: 99px;
  transition: width 0.6s ease;
}

/* Badges */
.dash-badge {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.25rem 0.65rem;
  border-radius: 99px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.badge-green { background: rgba(25,135,84,0.2); color: #4ade80; }
.badge-red   { background: rgba(201,14,14,0.2); color: #f87171; }
.badge-orange{ background: rgba(255,193,7,0.2); color: #fbbf24; }

/* Credential rows */
.dash-credential-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #3a2526;
}
.dash-credential-row:last-child { border-bottom: none; }
.dash-credential-label { color: #c0c0c0; font-size: 0.85rem; min-width: 80px; }
.dash-credential-value { display: flex; align-items: center; gap: 0.5rem; flex: 1; justify-content: flex-end; overflow: hidden; }
.dash-credential-value code { color: #c90e0e; font-size: 0.8rem; word-break: break-all; }

/* Credential blocks (line tab) */
.dash-cred-block {
  background: #0a0102;
  border: 1px solid #3a2526;
  border-radius: 8px;
  margin-bottom: 0.75rem;
}
.dash-cred-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #c0c0c0;
  font-size: 0.82rem;
  padding: 0.6rem 1rem;
  border-bottom: 1px solid #3a2526;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.dash-cred-value {
  display: block;
  color: #fff;
  font-size: 0.88rem;
  word-break: break-all;
  padding: 0.75rem 1rem;
  background: transparent;
}

/* Copy button */
.dash-copy-btn {
  background: transparent;
  border: 1px solid #3a2526;
  border-radius: 6px;
  color: #c0c0c0;
  cursor: pointer;
  padding: 0.2rem 0.5rem;
  font-size: 0.82rem;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  transition: all 0.2s;
  white-space: nowrap;
}
.dash-copy-btn:hover { border-color: #c90e0e; color: #c90e0e; }

/* Detail rows */
.dash-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0;
  border-bottom: 1px solid #3a2526;
  color: #c0c0c0;
  font-size: 0.9rem;
}
.dash-detail-row:last-child { border-bottom: none; }
.dash-detail-row strong { color: #fff; }

/* Profile form */
.dash-field-label { color: #c0c0c0; font-size: 0.85rem; display: block; margin-bottom: 0.4rem; }
.dash-input {
  width: 100%;
  background: #0a0102;
  border: 1px solid #3a2526;
  border-radius: 8px;
  color: #fff;
  padding: 0.65rem 1rem;
  font-size: 0.95rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s;
}
.dash-input:focus { border-color: #c90e0e; }

/* Alerts */
.dash-alert { padding: 0.85rem 1.25rem; border-radius: 8px; font-size: 0.9rem; }
.dash-alert-success { background: rgba(25,135,84,0.15); color: #4ade80; border: 1px solid rgba(25,135,84,0.3); }
.dash-alert-danger  { background: rgba(201,14,14,0.15); color: #f87171; border: 1px solid rgba(201,14,14,0.3); }

/* Setup steps */
.dash-setup-steps {
  display: flex;
  align-items: center;
  margin-bottom: 1.75rem;
  flex-wrap: wrap;
  gap: 0.25rem;
}
.dash-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  color: #555;
  min-width: 60px;
}
.dash-step.active { color: #fff; }
.dash-step.done { color: #4ade80; }
.dash-step-num {
  width: 32px; height: 32px;
  border-radius: 50%;
  border: 2px solid #3a2526;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.85rem; font-weight: 700;
  background: #0a0102;
  color: #555;
}
.dash-step.active .dash-step-num { border-color: #c90e0e; color: #c90e0e; }
.dash-step.done .dash-step-num { border-color: #198754; background: #198754; color: #fff; }
.dash-step-line {
  flex: 1;
  height: 2px;
  background: #3a2526;
  min-width: 24px;
  margin-bottom: 20px;
}
.dash-step-line.done { background: #198754; }

/* App download cards */
.dash-app-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}
.dash-app-card {
  background: #0a0102;
  border: 1px solid #3a2526;
  border-radius: 10px;
  padding: 1.1rem 0.75rem;
  text-align: center;
  text-decoration: none;
  transition: border-color 0.2s, transform 0.2s;
  display: block;
}
.dash-app-card:hover { border-color: #c90e0e; transform: translateY(-2px); }
.dash-app-name { color: #fff; font-weight: 600; font-size: 0.85rem; margin-top: 0.5rem; }
.dash-app-platform { color: #666; font-size: 0.72rem; margin-top: 0.2rem; }

/* MAC display */
.dash-mac-display {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: #0a0102;
  border: 1px solid #3a2526;
  border-radius: 8px;
  padding: 0.6rem 1.1rem;
  color: #c90e0e;
  font-family: monospace;
  font-size: 1rem;
  margin: 0.5rem auto;
}
.dash-mac-display i { color: #666; }

/* Text button */
.dash-text-btn {
  background: transparent;
  border: none;
  color: #c90e0e;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.85rem;
  padding: 0;
  text-decoration: underline;
}
.dash-text-btn:hover { color: #ff6b6b; }

/* Responsive */
@media (max-width: 768px) {
  .dashboard-layout { flex-direction: column; }
  .dash-sidebar {
    width: 100%;
    height: auto;
    position: static;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 1rem;
  }
  .dash-profile { display: none; }
  .dash-nav { flex-direction: row; flex-wrap: wrap; gap: 0.25rem; }
  .dash-logout { margin-top: 0; }
  .dash-main { padding: 1.5rem 1rem; }
}
</style>
